import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { TrendingUp, Filter } from "lucide-react";
import { clubInfo } from "@/lib/mbti";

/**
 * ConversionSummary — taxa de conversão ponderada média (Liga + por clube MBTI).
 * Props:
 *  - highlightClub: string | null — destaca o clube do coach
 *  - compact: boolean — versão reduzida para topo de páginas
 */
export default function ConversionSummary({ highlightClub = null, compact = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/rankings/clubs-summary")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.message || "Erro"));
  }, []);

  if (err) return null;
  if (!data) {
    return (
      <div className="kw-card p-6 text-[#737373] text-sm" data-testid="conversion-summary-loading">
        A carregar taxa de conversão da liga…
      </div>
    );
  }

  return (
    <div className="kw-card p-6" data-testid="conversion-summary">
      <SummaryHeader data={data} compact={compact} />
      <ClubGrid clubs={data.clubs || []}
        leagueAvg={data.league_avg_weighted_conversion}
        highlightClub={highlightClub}
        compact={compact} />
      {!compact && <Footnote />}
    </div>
  );
}

function SummaryHeader({ data, compact }) {
  const leaguePct = (data.league_avg_weighted_conversion * 100).toFixed(1);
  return (
    <div className="flex items-center justify-between mb-4 border-b border-[#222] pb-3 flex-wrap gap-3">
      <div>
        <div className="kw-overline">Eficiência {compact ? "do meu clube" : "da liga"}</div>
        <h2 className="font-display text-xl md:text-2xl font-black uppercase">
          {compact ? "Taxa Média de Conversão" : "Conversão Ponderada Média"}
        </h2>
        <p className="text-xs text-[#737373] mt-1">
          Fórmula: 20%·(R/C) + 60%·(A/R) + 20%·(V/A) · Média entre consultores com ≥1 contacto.
        </p>
      </div>
      <div className="text-right">
        <div className="kw-overline">Liga</div>
        <div className="font-display text-3xl md:text-4xl font-black text-[#E50000]" data-testid="league-avg-value">
          {leaguePct}%
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#737373]">
          {data.active_players} consultores ativos
        </div>
      </div>
    </div>
  );
}

function ClubGrid({ clubs, leagueAvg, highlightClub, compact }) {
  return (
    <div className={`grid gap-3 ${compact ? "md:grid-cols-2" : "md:grid-cols-4"}`}>
      {clubs.map((c, idx) => (
        <ClubCard key={c.club} club={c}
          isLeader={idx === 0 && clubs.length > 1}
          isHighlight={highlightClub && c.club === highlightClub}
          aboveLeague={c.avg_weighted_conversion > leagueAvg}
          compact={compact} />
      ))}
    </div>
  );
}

function ClubCard({ club: c, isLeader, isHighlight, aboveLeague, compact }) {
  const info = clubInfo(c.club);
  const pct = c.avg_weighted_conversion * 100;
  return (
    <div
      className="border p-4 bg-[#0A0A0A] relative"
      style={{
        borderColor: isHighlight ? "#E50000" : (info?.border || "#222"),
        boxShadow: isHighlight ? "0 0 0 1px #E50000 inset" : undefined,
      }}
      data-testid={`club-conv-${c.club.toLowerCase()}`}
    >
      {isLeader && (
        <div className="absolute -top-2 right-3 bg-[#E50000] text-white text-[9px] uppercase tracking-widest font-bold px-2 py-0.5">
          Líder
        </div>
      )}
      {isHighlight && (
        <div className="absolute -top-2 left-3 bg-[#E50000] text-white text-[9px] uppercase tracking-widest font-bold px-2 py-0.5">
          Meu Clube
        </div>
      )}
      <div className="flex items-center gap-2 mb-2">
        <div className="kw-overline" style={{ color: info?.color }}>{c.club}</div>
        {aboveLeague && <TrendingUp className="w-3 h-3 text-[#00FF88]" title="Acima da média da liga" />}
      </div>
      <div className="font-display text-3xl font-black" style={{ color: info?.color }} data-testid={`club-conv-pct-${c.club.toLowerCase()}`}>
        {pct.toFixed(1)}%
      </div>
      <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
        {c.active_players} consultor{c.active_players === 1 ? "" : "es"}
      </div>
      {!compact && (
        <div className="mt-3 pt-3 border-t border-[#222] grid grid-cols-3 gap-1 text-center">
          <SubRate label="R/C" pct={c.avg_meeting_rate} />
          <SubRate label="A/R" pct={c.avg_listing_rate} highlight />
          <SubRate label="V/A" pct={c.avg_sale_rate} />
        </div>
      )}
    </div>
  );
}

function Footnote() {
  return (
    <div className="mt-4 pt-4 border-t border-[#222] flex items-center gap-2 text-xs text-[#A3A3A3]">
      <Filter className="w-3 h-3" />
      O peso maior (60%) está em <strong className="text-white">Angariações/Reuniões</strong> — refletindo a qualidade da
      captação e do trabalho de pipeline. Clubes acima da média da liga têm o ícone <TrendingUp className="w-3 h-3 inline text-[#00FF88]" />.
    </div>
  );
}

function SubRate({ label, pct, highlight }) {
  return (
    <div>
      <div className={`font-display text-sm font-black ${highlight ? "text-[#E50000]" : ""}`}>
        {(pct * 100).toFixed(0)}%
      </div>
      <div className="text-[9px] uppercase tracking-widest text-[#737373]">{label}</div>
    </div>
  );
}
